import React, { useState } from 'react';
import ExcelJS from 'exceljs';
import Select from 'react-select';
import { styleSelect } from '../../../../../rbts/assets/ts/_utils';
import { formatPrice, LoadingGrid } from '../../../../../rbts/helpers';
import { useObtenerDiasPorEvento } from '../../cortesias/hooks/CortesiasHook';
import { useReporteRemanente } from '../hooks/RemanentesHook';
import { TicketRemanente } from '../../../../service/reportes/ReporteRemanente';

const bgStyle = { backgroundColor: '#f0f1f5', boxShadow: 'none', border: 'none' };

const Vista: React.FC = () => {
  const [diasSeleccionados, setDiasSeleccionados] = useState<any[]>([]);
  const [loadingDownload, setLoadingDownload] = useState(false);

  const { list: listaDias, loadingRequest: cargandoDias } = useObtenerDiasPorEvento(0);
  const idsDia = diasSeleccionados.map((d: any) => d.value as number);
  const { list, loading } = useReporteRemanente(idsDia, 0);

  const totalCargado   = list.reduce((s, r) => s + parseFloat(r.total_cargado as any   ?? 0), 0);
  const totalConsumido = list.reduce((s, r) => s + parseFloat(r.total_consumido as any ?? 0), 0);
  const totalRemanente = list.reduce((s, r) => s + parseFloat(r.saldo_remanente as any ?? 0), 0);

  const handleDownloadExcel = async () => {
    setLoadingDownload(true);
    try {
      const workbook = new ExcelJS.Workbook();
      const ws = workbook.addWorksheet('Remanente de Tickets');

      ws.columns = [
        { header: 'Folio',            key: 'folio_ticket',    width: 14 },
        { header: 'Cliente',          key: 'cliente',         width: 28 },
        { header: 'Evento',           key: 'evento',          width: 28 },
        { header: 'Total Cargado',    key: 'total_cargado',   width: 16 },
        { header: 'Total Consumido',  key: 'total_consumido', width: 16 },
        { header: 'Saldo Remanente',  key: 'saldo_remanente', width: 16 },
        { header: 'Fecha Creación',   key: 'fecha_creacion',  width: 20 },
      ];

      ws.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
      ws.getRow(1).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF1e3a5f' },
      };
      ws.getRow(1).alignment = { vertical: 'middle', horizontal: 'center' };

      list.forEach((r: TicketRemanente) => {
        ws.addRow({
          folio_ticket:    r.folio_ticket,
          cliente:         r.cliente,
          evento:          r.evento,
          total_cargado:   parseFloat(r.total_cargado as any   ?? 0),
          total_consumido: parseFloat(r.total_consumido as any ?? 0),
          saldo_remanente: parseFloat(r.saldo_remanente as any ?? 0),
          fecha_creacion:  r.fecha_creacion ? new Date(r.fecha_creacion).toLocaleDateString('es-MX') : '—',
        });
      });

      // Fila de totales
      const totalRow = ws.addRow({
        folio_ticket:    'TOTALES',
        cliente:         '',
        evento:          '',
        total_cargado:   totalCargado,
        total_consumido: totalConsumido,
        saldo_remanente: totalRemanente,
        fecha_creacion:  '',
      });
      totalRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      totalRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2d5986' } };

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `reporte_remanente_tickets.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } finally {
      setLoadingDownload(false);
    }
  };

  return (
    <>
      {/* ── Selector de días ──────────────────────────────────── */}
      <div className="card mb-6 mb-xl-9">
        <div className="card-header border-0">
          <h3 className="card-title align-items-start flex-column">
            <span className="card-label fw-bold text-gray-800">Búsqueda</span>
          </h3>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-sm-12 col-md-6 col-lg-5 col-xl-4">
              <div className="fs-7 text-gray-700">
                <div className="w-100 d-flex">
                  <label className="form-label w-auto">Días administrativos:</label>
                </div>
                <Select
                  options={listaDias}
                  value={diasSeleccionados}
                  styles={styleSelect}
                  placeholder="Seleccione uno o varios días..."
                  className="w-100"
                  isSearchable
                  isMulti
                  closeMenuOnSelect={false}
                  isClearable
                  isLoading={cargandoDias}
                  noOptionsMessage={() => <span>No se encontraron registros</span>}
                  onChange={(opts: any) => setDiasSeleccionados(opts ?? [])}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Tabla de resultados ───────────────────────────────── */}
      <div className="card card-xxl-stretch mb-5 mb-xl-12">
        <div className="card-header border-0">
          <h3 className="card-title align-items-start flex-column">
            <span className="card-label fw-bold text-gray-800">
              Remanente de Tickets
            </span>
            {list.length > 0 && (
              <span className="text-muted mt-1 fw-semibold fs-7">
                {list.length} ticket{list.length !== 1 ? 's' : ''} con saldo pendiente
              </span>
            )}
          </h3>
          <div className="card-toolbar">
            {list.length > 0 && (
              <button
                className="btn btn-sm btn-light-success"
                onClick={handleDownloadExcel}
                disabled={loadingDownload}
              >
                {loadingDownload ? (
                  <span className="indicator-progress" style={{ display: 'block' }}>
                    Descargando...
                    <span className="spinner-border spinner-border-sm align-middle ms-2" />
                  </span>
                ) : (
                  'Descargar Excel'
                )}
              </button>
            )}
          </div>
        </div>
        <div className="card-body py-3">
          {loading ? (
            <LoadingGrid text="Cargando reporte..." />
          ) : idsDia.length === 0 ? (
            <div className="text-center py-10 text-muted fs-6">
              Selecciona al menos un día administrativo para ver el reporte.
            </div>
          ) : list.length === 0 ? (
            <div className="text-center py-10 text-muted fs-6">
              No hay tickets con remanente para los días seleccionados.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover table-rounded table-striped border gy-4 gs-7">
                <thead>
                  <tr className="fw-bolder fs-7 text-gray-800 border-bottom border-gray-200">
                    <th>Folio</th>
                    <th>Cliente</th>
                    <th>Evento</th>
                    <th className="text-end">Total Cargado</th>
                    <th className="text-end">Total Consumido</th>
                    <th className="text-end">Saldo Remanente</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((r: TicketRemanente, i) => (
                    <tr key={r.folio_ticket + i} className="align-middle">
                      <td className="fw-bold fs-7" style={{ color: '#1e3a5f' }}>{r.folio_ticket}</td>
                      <td className="fs-7">{r.cliente}</td>
                      <td className="fs-7">{r.evento}</td>
                      <td className="text-end fs-7">
                        {formatPrice(parseFloat(r.total_cargado as any ?? 0), 'es-MX', 'MXN', 2, 2)}
                      </td>
                      <td className="text-end fs-7">
                        {formatPrice(parseFloat(r.total_consumido as any ?? 0), 'es-MX', 'MXN', 2, 2)}
                      </td>
                      <td className="text-end fs-7 fw-bold" style={{ color: '#1a7a4a' }}>
                        {formatPrice(parseFloat(r.saldo_remanente as any ?? 0), 'es-MX', 'MXN', 2, 2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ fontWeight: 600 }}>
                    <td colSpan={3} className="fs-7 fw-bolder ps-3" style={{ ...bgStyle, color: '#1e3a5f' }}>
                      Totales ({list.length} ticket{list.length !== 1 ? 's' : ''})
                    </td>
                    <td className="text-end fs-7" style={{ ...bgStyle, color: '#1a7a4a' }}>
                      {formatPrice(totalCargado, 'es-MX', 'MXN', 2, 2)}
                    </td>
                    <td className="text-end fs-7" style={{ ...bgStyle, color: '#c0392b' }}>
                      {formatPrice(totalConsumido, 'es-MX', 'MXN', 2, 2)}
                    </td>
                    <td className="text-end fs-7 fw-bolder" style={{ ...bgStyle, color: '#1a7a4a' }}>
                      {formatPrice(totalRemanente, 'es-MX', 'MXN', 2, 2)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export { Vista };
